// ============================================================================
// MoreMenuSheet — tiroir « Plus » du GsMobileBottomNav (E1a, mobile <md)
// ============================================================================
// §17 : 4 onglets max en barre basse → le surplus (Organigramme / Affichages /
// Rencontres CG / Comités / Commentaires) vit derrière l'onglet « Plus », en
// BOTTOM SHEET (§4c « Surfaces superposées » : mobile = bottom sheet, pattern
// FilterSidebar). Composant APP-LOCAL (pas @gs/ui — 1re implémentation ; à
// promouvoir si une 2e app en a besoin, DS-013). Fermeture Échap / backdrop.
// Cibles tactiles ≥44px (min-h-11), touch-manipulation, dark-aware, zéro hex.

import { useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { GsIconButton } from '@gs/ui';

export interface MoreMenuItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string;
  badge?: string | number;
}

export interface MoreMenuSheetProps {
  open: boolean;
  onClose: () => void;
  items: MoreMenuItem[];
  onNavigate: (path: string) => void;
  /** Titre du tiroir (i18n — « Plus »). */
  title: string;
  /** Libellé a11y du bouton fermer. */
  closeLabel: string;
}

export function MoreMenuSheet({ open, onClose, items, onNavigate, title, closeLabel }: MoreMenuSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
      />
      <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-slate-200 bg-white p-3 pb-6 shadow-md dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-1 flex items-center justify-between px-1">
          <h2 className="text-field font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
          <GsIconButton
            icon={<X size={18} aria-hidden />}
            tooltip={closeLabel}
            variant="ghost"
            size="lg"
            aria-label={closeLabel}
            onClick={onClose}
            className="min-h-11 min-w-11 touch-manipulation"
          />
        </div>
        <nav>
          {items.map(({ key, label, icon: Icon, path, badge }) => (
            <button
              key={key}
              type="button"
              onClick={() => { onClose(); onNavigate(path); }}
              className="flex min-h-11 w-full touch-manipulation items-center gap-3 rounded-lg px-3 py-2.5 text-left text-body text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Icon size={18} className="shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{label}</span>
              {badge != null && badge !== 0 && badge !== '' && (
                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-chip font-semibold tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-200">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
